import { useState } from "react";
import { Document as PDFDocument, Page as PDFPage, pdfjs } from "react-pdf";
import { ZoomIn, ZoomOut, RotateCwSquare, RefreshCcw } from "lucide-react";
import { Button } from "@/components/ui/button-new";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

// Initialize PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

interface PdfViewerProps {
    file: string;
    alt?: string;
}

export function PdfViewer({ file, alt = "PDF Document" }: PdfViewerProps) {
    const [numPages, setNumPages] = useState<number | null>(null);
    // Track container width to make PDF pages responsive
    const [containerWidth, setContainerWidth] = useState<number>(0);
    // Zoom scale factor (1.0 = 100%)
    const [scale, setScale] = useState<number>(1.0);
    // Rotation in degrees (0, 90, 180, 270)
    const [rotation, setRotation] = useState<number>(0);

    function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
        setNumPages(numPages);
    }

    return (
        <div className="w-full h-full flex flex-col">
            {/* Zoom and rotation controls */}
            <div className="flex gap-1 p-2 justify-center">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setScale((s) => Math.min(s + 0.25, 3))}
                    disabled={scale >= 3}
                >
                    <ZoomIn className="h-4 w-4" />
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setScale((s) => Math.max(s - 0.25, 0.5))}
                    disabled={scale <= 0.5}
                >
                    <ZoomOut className="h-4 w-4" />
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                        setScale(1);
                        setRotation(0);
                    }}
                    disabled={scale === 1 && rotation === 0}
                >
                    <RefreshCcw className="h-4 w-4" />
                </Button>
                <span className="text-sm flex items-center px-2 w-16 justify-center">
                    {Math.round(scale * 100)}%
                </span>

                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setRotation((r) => (r + 90) % 360)}
                >
                    <RotateCwSquare className="h-4 w-4" />
                </Button>
            </div>

            {/* PDF viewer */}
            <div
                className="flex-1 overflow-auto"
                role="img"
                aria-label={alt}
                // Measure container width on mount for responsive PDF scaling
                ref={(el) => {
                    if (el) setContainerWidth(el.offsetWidth);
                }}
            >
                <PDFDocument
                    file={file}
                    onLoadSuccess={onDocumentLoadSuccess}
                    loading={
                        <div className="flex items-center justify-center h-full">
                            Loading PDF...
                        </div>
                    }
                    error={
                        <div className="text-red-500">Failed to load PDF</div>
                    }
                >
                    {Array.from(new Array(numPages || 0), (_, index) => (
                        <PDFPage
                            key={`page_${index + 1}`}
                            pageNumber={index + 1}
                            renderTextLayer={true}
                            renderAnnotationLayer={true}
                            className="mb-4"
                            // Scale PDF to fit container width with zoom multiplier
                            width={
                                containerWidth
                                    ? containerWidth * scale
                                    : undefined
                            }
                            // Rotate PDF (0, 90, 180, 270 degrees)
                            rotate={rotation}
                            loading={<div />}
                        />
                    ))}
                </PDFDocument>
            </div>
        </div>
    );
}
